import { useEffect, useRef, useCallback } from 'react';
import { taskEventService, TaskEvent, TaskEventListener } from '../services/api/task-events.service';

/**
 * React hook for subscribing to real-time task events
 * Automatically manages SSE connection lifecycle and provides event callbacks
 */
export interface UseTaskEventsOptions {
  /**
   * Called when any task event is received
   */
  onTaskEvent?: (event: TaskEvent) => void;

  /**
   * Called when a new task is created
   */
  onTaskCreated?: (event: TaskEvent) => void;

  /**
   * Called when a task is updated
   */
  onTaskUpdated?: (event: TaskEvent) => void;

  /**
   * Called when a task is deleted
   */
  onTaskDeleted?: (event: TaskEvent) => void;

  /**
   * Called when a task status changes
   */
  onTaskStatusChanged?: (event: TaskEvent) => void;

  /**
   * Called when a task is assigned
   */
  onTaskAssigned?: (event: TaskEvent) => void;

  /**
   * Called when connection is established
   */
  onConnected?: () => void;

  /**
   * Called when connection fails
   */
  onError?: (error: Error) => void;

  /**
   * Auto-connect on hook mount (default: true)
   */
  autoConnect?: boolean;
}

export interface UseTaskEventsReturn {
  /**
   * Current connection status
   */
  isConnected: boolean;

  /**
   * Whether reconnection is in progress
   */
  isReconnecting: boolean;

  /**
   * Manually connect to task events stream
   */
  connect: () => Promise<void>;

  /**
   * Disconnect from task events stream
   */
  disconnect: () => void;

  /**
   * Force reconnect (useful for error recovery)
   */
  reconnect: () => Promise<void>;
}

/**
 * Hook for managing real-time task event subscriptions
 *
 * @example
 * ```typescript
 * const { isConnected, connect } = useTaskEvents({
 *   onTaskCreated: (event) => {
 *     console.log('New task created:', event.data);
 *   },
 *   onTaskStatusChanged: (event) => {
 *     console.log('Task status changed:', event.data);
 *   },
 *   onError: (error) => {
 *     console.error('Task events error:', error);
 *   }
 * });
 * ```
 */
export const useTaskEvents = (options: UseTaskEventsOptions = {}): UseTaskEventsReturn => {
  const {
    onError,
    autoConnect = true
  } = options;

  // Use refs to avoid stale closures in event handlers
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Event listener that routes events to appropriate callbacks
  const eventListener: TaskEventListener = useCallback((event: TaskEvent) => {
    const currentOptions = optionsRef.current;

    // Call general event handler
    if (currentOptions.onTaskEvent) {
      currentOptions.onTaskEvent(event);
    }

    // Route to specific event handlers
    switch (event.type) {
      case 'task_created':
        if (currentOptions.onTaskCreated) {
          currentOptions.onTaskCreated(event);
        }
        break;

      case 'task_updated':
        if (currentOptions.onTaskUpdated) {
          currentOptions.onTaskUpdated(event);
        }
        break;

      case 'task_deleted':
        if (currentOptions.onTaskDeleted) {
          currentOptions.onTaskDeleted(event);
        }
        break;

      case 'task_status_changed':
        if (currentOptions.onTaskStatusChanged) {
          currentOptions.onTaskStatusChanged(event);
        }
        break;

      case 'task_assigned':
        if (currentOptions.onTaskAssigned) {
          currentOptions.onTaskAssigned(event);
        }
        break;

      case 'connection_established':
        if (currentOptions.onConnected) {
          currentOptions.onConnected();
        }
        break;

      default:
        // Handle any other event types
        break;
    }
  }, []);

  // Connection management functions
  const connect = useCallback(async (): Promise<void> => {
    try {
      await taskEventService.connect();
    } catch (error) {
      console.error('[useTaskEvents] Connection failed:', error);
      if (onError) {
        onError(error as Error);
      }
    }
  }, [onError]);

  const disconnect = useCallback((): void => {
    taskEventService.disconnect();
  }, []);

  const reconnect = useCallback(async (): Promise<void> => {
    try {
      await taskEventService.reconnect();
    } catch (error) {
      console.error('[useTaskEvents] Reconnection failed:', error);
      if (onError) {
        onError(error as Error);
      }
    }
  }, [onError]);

  // Get connection status
  const getConnectionStatus = useCallback(() => {
    return taskEventService.getConnectionStatus();
  }, []);

  // Set up event listener and auto-connect
  useEffect(() => {
    // Add event listener
    taskEventService.addListener(eventListener);

    // Auto-connect if enabled
    if (autoConnect) {
      connect().catch((error) => {
        console.error('[useTaskEvents] Auto-connect failed:', error);
      });
    }

    // Cleanup on unmount
    return () => {
      taskEventService.removeListener(eventListener);
    };
  }, [eventListener, connect, autoConnect]);

  // Get current connection status
  const { connected, reconnecting } = getConnectionStatus();

  return {
    isConnected: connected,
    isReconnecting: reconnecting,
    connect,
    disconnect,
    reconnect
  };
};